


















































import { Component, Prop } from 'vue-property-decorator';
import DxDataSource from 'devextreme/data/data_source';
import { DxSelectBox } from 'devextreme-vue';
import { ContextsState, ProductsState } from '@/store';
import container from '@/container';
import {
    CONTRACTS,
    FetchServiceContract,
    WindowsServiceContract,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { Page } from '@/core/components/controls';
import {
    Table,
    TableOptions,
    getTableCaption,
    checkField,
} from '@/core/components/table';
import ProductRelocationsTable from './ProductRelocationsTable';
import { StructuredResult, ProductRelocations } from '@/dto';
import { encodeParams, lookupField } from '@/core/helpers/utils';
import { localize } from '@/core/filters';
import { Inject } from '@/core/helpers';
import { ProductRelocationsPageArgs } from './types';
import { getTableLayoutFromUserSettings } from '@/core/components/table';

@Component({
    components: {
        TableOptions,
        ProductRelocationsTable,
        DxSelectBox,
    },
})
export default class ProductRelocationsPage extends Page {
    @Prop({ default: () => ({}) }) public args!: ProductRelocationsPageArgs;
    public table: Table | null = null;
    public isSelectDismissCommentVisible: boolean = false;
    public selectedCommentValue: string | null = null;

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    public get dataSource() {
        return new DxDataSource({
            load: this.load,
            update: () => Promise.resolve(),
        });
    }

    public get tableKey(): string {
        return this.args!.onlyAgreements
            ? 'product-relocation-agreements-control'
            : 'product-relocations-control';
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public setTable(table: Table): void {
        this.table = table;
    }

    public async onSettingsChanged() {
        getTableLayoutFromUserSettings(this.tableKey).then(res => {
            this.table?.state(res);
            });
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
        this._eventsService!.on(
            Events.SettingsChanged,
            this.onSettingsChanged
        );

    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
        this._eventsService!.off(
            Events.SettingsChanged,
            this.onSettingsChanged
        );

    }

    public accept() {
        const items = this.getSelectedRows();

        if (items.length > 0) {
            this.storeAgreements(items, true);
        }
    }

    public openRefuseCommentSelection() {
        const items = this.getSelectedRows();

        if (items.length > 0) {
            this.isSelectDismissCommentVisible = true;
        }
    }

    public refuseWithComment(comment: string) {
        const items = this.getSelectedRows();

        for (const item of items) {
            item.agreementDescription = comment;
        }

        if (
            items.length > 0 &&
            checkField<ProductRelocations>(items, 'agreementDescription')
        ) {
            this.storeAgreements(items, false);
            this.isSelectDismissCommentVisible = false;
        } else {
            this._windowsService!.showDialog(localize('RefuseAgreementsFail'));
        }
    }

    public get productRelocationComments(): string[] {
        const comments = localize('ProductRelocationComments');
        return comments && Array.isArray(comments)
            ? (comments as string[])
            : [];
    }

    private storeAgreements(items: ProductRelocations[], accepted: boolean) {
        const currentContextId = ContextsState.currentContextId;
        const url = `/contexts/${currentContextId}/route-agreements`;
        const data = items.map(x => ({
            id: x.id,
            agreementDescription: x.agreementDescription,
            accepted,
        }));

        for (const item of items) {
            item.accepted = accepted;
        }

        container
            .get<FetchServiceContract>(CONTRACTS.FetchService)
            .post(url, data)
            .then(() => this.table!.refresh());
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    private async load(): Promise<ProductRelocations[]> {
        const currentContextId = ContextsState.currentContextId;
        const query = {} as any;

        if (ProductsState.current) {
            query.productAlias = ProductsState.current.alias;
        }

        if (this.args!.onlyAgreements !== undefined) {
            query.onlyAgreements = this.args!.onlyAgreements;
        }

        const url = `/contexts/${currentContextId}/scheduled-routes?${encodeParams(
            query
        )}`;
        return await container
            .get<FetchServiceContract>(CONTRACTS.FetchService)
            .get<StructuredResult<ProductRelocations>>(url)
            .then(this.transformData);
    }

    private transformData(
        data: StructuredResult<ProductRelocations>
    ): ProductRelocations[] {
        if (!(data && data.items)) {
            return [];
        }

        return data.items.map(item => {
            lookupField(item, 'destination', data.sites);
            lookupField(item, 'product', data.products);
            lookupField(item, 'source', data.sites);
            item.productType = item.product.typeId ? data.resourceTypes[item.product.typeId] : undefined;
            item.volume = item.quantity / (item.packVolumeDenominator ?? item.product.volumeDenominator);
            item.count = item.quantity / (item.packDisplayDenominator ?? item.product.displayDenominator);

            if (item.product.weightFactor || item.packWeightFactor) {
                item.weight = item.quantity * (item.packWeightFactor ?? item.product.weightFactor);
            }

            if (item.product.area) {
                item.packArea = item.quantity * item.product.area;
            }

            if (item.consignmentId) {
                item.consignment = data.strings[item.consignmentId];
            }

            return item;
        });
    }

    private getSelectedRows(): ProductRelocations[] {
        return this.table!.getSelectedRowsData() as ProductRelocations[];
    }
}
